import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { getBacktest, getBacktestProgress } from '../api';

// Drives the live-progress modal for any run that was offloaded to the backend
// (large single backtests, walk-forward, parameter sweeps, WFA optimization).
// Callers hand us a queued backtest id via `startRun(...)`; we poll it and read
// `backtest.progress` (written by the Celery tasks) to show "220 / 2000 bars
// analyzed". Closing the modal stops the fast poll — the persistent notification
// (NotificationCenter, 30s poll) then carries the run to completion.

const BackgroundRunsContext = createContext(null);

const KIND_LABELS = {
  backtest: 'Running backtest',
  walk_forward: 'Walk-forward analysis',
  param_sweep: 'Parameter sweep',
  optimization: 'WFA optimization',
};

// "analyzed" / "completed" reads naturally per unit; matches the backend wording.
const UNIT_VERB = { bars: 'analyzed', folds: 'completed', combos: 'completed', trials: 'completed' };

const POLL_MS = 2500;
const TERMINAL = new Set(['completed', 'failed']);

export function BackgroundRunsProvider({ children }) {
  // run: { backtestId, kind, label, status, progress, backtest, error }
  const [run, setRun] = useState(null);
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);
  const onCompleteRef = useRef(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => () => stopPoll(), [stopPoll]);

  const startRun = useCallback(({ backtestId, kind = 'backtest', label, onComplete }) => {
    if (!backtestId) return;
    stopPoll();
    onCompleteRef.current = onComplete || null;
    setRun({
      backtestId,
      kind,
      label: label || KIND_LABELS[kind] || 'Working…',
      status: 'running',
      progress: null,
      backtest: null,
      error: null,
    });
    setVisible(true);

    const poll = async () => {
      try {
        // Light poll: status + progress only, not the whole (large) backtest.
        const res = await getBacktestProgress(backtestId);
        const data = res.data || {};
        const prog = data.progress || null;
        // For sub-analyses (WFA/sweep/optimize) the parent backtest is already
        // "completed", so progress.status is the reliable terminal signal.
        const runStatus = prog?.status
          || (kind === 'backtest' ? data.status : 'running');

        setRun((prev) => (prev && prev.backtestId === backtestId
          ? { ...prev, status: runStatus, progress: prog }
          : prev));

        if (TERMINAL.has(runStatus)) {
          stopPoll();
          if (runStatus === 'completed') {
            // Fetch the full backtest once, so "View results" can hand it back.
            try {
              const full = await getBacktest(backtestId);
              setRun((prev) => (prev && prev.backtestId === backtestId
                ? { ...prev, backtest: full.data }
                : prev));
            } catch {
              // If the full fetch fails, the notification/results page still works.
            }
          }
        }
      } catch {
        // transient network/auth hiccup — keep polling
      }
    };
    poll();
    pollRef.current = setInterval(poll, POLL_MS);
  }, [stopPoll]);

  const closeModal = useCallback(() => {
    stopPoll();
    setVisible(false);
    setRun(null);
    onCompleteRef.current = null;
  }, [stopPoll]);

  const handleViewResults = useCallback(async () => {
    const cb = onCompleteRef.current;
    const id = run?.backtestId;
    let data = run?.backtest;
    stopPoll();
    setVisible(false);
    setRun(null);
    onCompleteRef.current = null;
    if (!cb) return;
    // The full backtest is usually prefetched on completion; fetch it if the
    // user clicked before that landed.
    if (!data && id) {
      try { data = (await getBacktest(id)).data; } catch { data = null; }
    }
    if (data) cb(data);
  }, [run, stopPoll]);

  return (
    <BackgroundRunsContext.Provider value={{ startRun }}>
      {children}
      {visible && run && (
        <BackgroundRunModal run={run} onClose={closeModal} onViewResults={handleViewResults} />
      )}
    </BackgroundRunsContext.Provider>
  );
}

export function useBackgroundRuns() {
  const ctx = useContext(BackgroundRunsContext);
  if (!ctx) return { startRun: () => {} };
  return ctx;
}

function BackgroundRunModal({ run, onClose, onViewResults }) {
  const { kind, label, status, progress } = run;
  const done = status === 'completed';
  const failed = status === 'failed';
  const running = !done && !failed;

  const current = progress?.current ?? 0;
  const total = progress?.total ?? 0;
  const unit = progress?.unit || (kind === 'backtest' ? 'bars' : 'items');
  const verb = UNIT_VERB[unit] || 'processed';
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : null;

  const title = done ? `${KIND_LABELS[kind] || 'Run'} complete`
    : failed ? `${KIND_LABELS[kind] || 'Run'} failed`
    : label;

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 24, maxWidth: 440, width: '100%', position: 'relative',
      }}>
        <button
          onClick={onClose}
          title="Hide"
          style={{
            position: 'absolute', top: 14, right: 14, background: 'none', border: 'none',
            color: 'var(--muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1,
          }}
        >✕</button>

        <h3 style={{
          margin: '0 0 4px', paddingRight: 24, color: 'var(--text)', fontSize: 17,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {done ? '✅ ' : failed ? '⚠️ ' : ''}{title}
        </h3>

        {running && (
          <>
            <p style={{ color: 'var(--muted)', margin: '0 0 16px', fontSize: 13 }}>
              This run is executing in the background. You can close this and it will keep
              going — a notification will let you know when it’s done.
            </p>

            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text)' }}>
              <span>
                {total > 0
                  ? `${current.toLocaleString()} / ${total.toLocaleString()} ${unit} ${verb}`
                  : 'Starting…'}
              </span>
              {pct !== null && <span style={{ color: 'var(--muted)' }}>{pct}%</span>}
            </div>

            <div style={{
              height: 8, borderRadius: 999, background: 'var(--border)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: pct !== null ? `${pct}%` : '25%',
                background: 'var(--accent, #2f81f7)',
                borderRadius: 999,
                transition: 'width 0.4s ease',
                animation: pct === null ? 'algovexIndeterminate 1.2s ease-in-out infinite' : undefined,
              }} />
            </div>
          </>
        )}

        {done && (
          <>
            <p style={{ color: 'var(--muted)', margin: '0 0 18px', fontSize: 13 }}>
              {total > 0
                ? `Finished — ${total.toLocaleString()} ${unit} ${verb}.`
                : 'Finished.'}
            </p>
            <button
              onClick={onViewResults}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8, border: 'none',
                background: 'var(--accent, #2f81f7)', color: '#fff', fontWeight: 600,
                fontSize: 14, cursor: 'pointer',
              }}
            >
              View results
            </button>
          </>
        )}

        {failed && (
          <>
            <p style={{ color: 'var(--muted)', margin: '0 0 18px', fontSize: 13 }}>
              Something went wrong while running this in the background. Please try again.
            </p>
            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text)', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              Close
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes algovexIndeterminate {
          0% { margin-left: -25%; }
          100% { margin-left: 100%; }
        }
      `}</style>
    </div>
  );
}
